import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import type { Listing } from "@/lib/types";

export function useAllListings(search?: string, city?: string, state?: string, service?: string, zip?: string) {
  return useQuery<Listing[]>({
    queryKey: ["listings", "all", search, city, state, service, zip],
    queryFn: async () => {
      let query = supabase
        .from("listings")
        .select("*")
        .eq("status", "active")
        .order("is_featured", { ascending: false })
        .order("name", { ascending: true });

      if (search) {
        query = query.or(`name.ilike.%${search}%,zip.ilike.%${search}%,address.ilike.%${search}%`);
      }
      if (city) {
        query = query.eq("city", city);
      }
      if (state) {
        query = query.eq("state", state);
      }
      if (service) {
        query = query.contains("tags", [service]);
      }
      if (zip) {
        query = query.ilike("zip", `${zip}%`);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data as Listing[];
    },
  });
}

export function useListingStates() {
  return useQuery<string[]>({
    queryKey: ["listings", "states"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("listings")
        .select("state")
        .eq("status", "active")
        .not("state", "is", null)
        .order("state");

      if (error) throw error;
      const unique = [...new Set(data.map((d: { state: string }) => d.state))];
      return unique;
    },
  });
}

export function useListingCities() {
  return useQuery<string[]>({
    queryKey: ["listings", "cities"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("listings")
        .select("city")
        .eq("status", "active")
        .not("city", "is", null)
        .order("city");

      if (error) throw error;
      const unique = [...new Set(data.map((d: { city: string }) => d.city))];
      return unique;
    },
  });
}

export function useListingTags() {
  return useQuery<string[]>({
    queryKey: ["listings", "tags"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("listings")
        .select("tags")
        .eq("status", "active");

      if (error) throw error;
      const all = data.flatMap((d: { tags: string[] }) => d.tags || []);
      const unique = [...new Set(all)].sort();
      return unique;
    },
  });
}

export function useFeaturedListings() {
  return useQuery<Listing[]>({
    queryKey: ["listings", "featured"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("listings")
        .select("*")
        .eq("status", "active")
        .eq("is_featured", true)
        .order("rating", { ascending: false })
        .limit(6);

      if (error) throw error;
      return data as Listing[];
    },
  });
}

/**
 * Find up to `limit` other active listings in the same city/state, excluding the current one.
 * Used on spa profile pages for the "Nearby MedSpas" internal-linking section.
 */
export function useNearbyListings(opts: {
  city: string | null | undefined;
  state: string | null | undefined;
  excludeSlug: string | null | undefined;
  limit?: number;
}) {
  const { city, state, excludeSlug, limit = 6 } = opts;
  return useQuery<Listing[]>({
    queryKey: ["listings", "nearby", state, city, excludeSlug, limit],
    enabled: !!state,
    queryFn: async () => {
      // First try same city + state
      const tryQuery = async (filters: { city?: string; state: string }) => {
        let q = supabase
          .from("listings")
          .select("*")
          .eq("status", "active")
          .eq("state", filters.state)
          .order("is_featured", { ascending: false })
          .order("rating", { ascending: false })
          .limit(limit + 1);
        if (filters.city) q = q.eq("city", filters.city);
        const { data, error } = await q;
        if (error) throw error;
        return ((data as Listing[]) || []).filter((l) => l.slug !== excludeSlug).slice(0, limit);
      };

      if (city) {
        const sameCity = await tryQuery({ city, state: state! });
        if (sameCity.length >= 3) return sameCity;
        // Fall through and top up with state-level matches if we got too few
        const stateLevel = await tryQuery({ state: state! });
        const seen = new Set(sameCity.map((l) => l.slug));
        const merged = [...sameCity, ...stateLevel.filter((l) => !seen.has(l.slug))].slice(0, limit);
        return merged;
      }
      return tryQuery({ state: state! });
    },
  });
}

export function useListingBySlug(slug: string | undefined) {
  return useQuery<Listing | null>({
    queryKey: ["listings", slug],
    enabled: !!slug,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("listings")
        .select("*")
        .eq("slug", slug!)
        .eq("status", "active")
        .single();

      if (error) {
        if (error.code === "PGRST116") return null; // not found
        throw error;
      }
      return data as Listing;
    },
  });
}
